<template>
  <section class='p-1'>
    <p class='documents-title'>
      Contrato de adesão, termos de uso e política de privacidade
    </p>

    <div class='documents' >
      <article
        v-for='doc in documents'
        :key='doc.acceptedKey'
        class='document-card'
      >
        <div class='document-card__body'>
          <header class='document-card__header'>
            <b-icon :icon='doc.icon' class='document-card__icon' />
            <span class='document-card__title'>{{ doc.title }}</span>
          </header>

          <hr class='document-card__divider' />

          <p class='document-card__description'>{{ doc.description }}</p>

          <a
            :href='doc.link'
            target='_blank'
            rel='noopener'
            class='document-card__link'
          >
            Acessar documento completo
            <b-icon icon='arrow-right' />
          </a>
        </div>

        <div class='document-card__footer'>
          <b-form-checkbox v-model='billingData[doc.acceptedKey]'>
            <strong>{{ doc.declaration }}</strong>
          </b-form-checkbox>
        </div>
      </article>
    </div>

    <div class='mt-3'>
      <b-alert
        show
        variant='danger'
        :class='{ hidden: !errors }'
      >
        {{ errors }}
      </b-alert>
    </div>

    <section class='buttons d-flex justify-content-between align-items-center'>
      <base-button
        class='nav-btn'
        dark
        @click='back()'
      >
        Voltar
      </base-button>

      <base-button
        class='nav-btn'
        @click='next()'
      >
        Próximo
      </base-button>
    </section>

  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { required } from '@/utils/validators'
import useVuelidate from '@vuelidate/core'

export default {
  name: 'AdhesionContract',
  setup() {
    return { v$: useVuelidate() }
  },
  props: {},
  data() {
    return {
      acceptedTerms: false
    }
  },
  computed: {
    term() {
      return this.$store.getters['term/term']
    },
    errors() {
      return this.$store.getters['access/error']
    },
    ...mapGetters('checkout2', {
      billingData: 'getBillingData'
    }),
    documents() {
      return [
        {
          icon: 'file-earmark-text',
          title: 'Documento: Termos de Uso',
          description: 'Estabelece as regras de utilização da plataforma, responsabilidades do usuário, limitações de serviço e propriedade intelectual.',
          declaration: 'Declaro que acessei o documento completo, li e estou ciente e de acordo com as condições estabelecidas no Termos de Uso.',
          link: this.term.termsOfUseUrl,
          acceptedKey: 'termsOfUseAccepted'
        },
        {
          icon: 'shield-lock',
          title: 'Documento: Política de Privacidade',
          description: 'Detalha como coletamos, usamos, armazenamos e protegemos seus dados pessoais de acordo com a LGPD.',
          declaration: 'Declaro que acessei o documento completo, li e estou ciente e de acordo com as práticas descritas na Política de Privacidade.',
          link: this.term.privacyPolicyUrl,
          acceptedKey: 'privacyPolicyAccepted'
        },
        {
          icon: 'file-earmark-text',
          title: 'Documento: Contrato de Adesão',
          description: 'Define as condições específicas da prestação de serviços, prazos, garantias e obrigações mútuas da contratação.',
          declaration: 'Declaro que acessei o documento completo, li e estou ciente e de acordo com as condições do Contrato de Adesão.',
          link: this.term.adhesionContractUrl,
          acceptedKey: 'adhesionContractAccepted'
        }
      ]
    }
  },
  validations() {
    return {
      billingData: {
        termsOfUseAccepted: { required },
        privacyPolicyAccepted: { required },
        adhesionContractAccepted: { required }
      }
    }
  },
  methods: {
    ...mapActions('term', {
      handleGet: 'handleGet'
    }),
    async next() {
      await this.$store.dispatch('access/updateError', ``)
      if (
        !this.billingData.termsOfUseAccepted ||
        !this.billingData.privacyPolicyAccepted ||
        !this.billingData.adhesionContractAccepted
      ) {
        await this.$store.dispatch('access/updateError', `Acesse cada documento acima e assinale as três declarações para poder prosseguir com a adesão do plano a ser contratado neste checkout.`)
        return
      }
      this.$emit('next-step')
    },
    async back() {
      await this.$router.push('/planos')
    }
  },

  async created() {
    try {
      await this.$store.dispatch('access/updateError', ``)
      await this.handleGet()
      this.billingData.adhesionContractUrl = this.term.adhesionContractUrl
      this.billingData.adhesionContractDate = this.term.adhesionContractDate
      this.billingData.adhesionContractName = this.term.adhesionContractName
      this.billingData.termsOfUseUrl = this.term.termsOfUseUrl
      this.billingData.termsOfUseDate = this.term.termsOfUseDate
      this.billingData.termsOfUseName = this.term.termsOfUseName
      this.billingData.privacyPolicyUrl = this.term.privacyPolicyUrl
      this.billingData.privacyPolicyDate = this.term.privacyPolicyDate
      this.billingData.privacyPolicyName = this.term.privacyPolicyName
    } catch (e) {

    }
  }
}
</script>

<style lang='scss' scoped>
.documents-title {
  color: #262626;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  margin-bottom: 16px;
}

.documents {
  display: flex;
  gap: 16px;
  align-items: stretch;

  @media only screen and (max-width: 720px) {
    flex-direction: column;
  }
}

.document-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--white);
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;

  &__body {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 16px;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__icon {
    color: #262626;
    width: 18px;
    height: 18px;
  }

  &__title {
    color: #262626;
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    text-transform: uppercase;
  }

  &__divider {
    width: 100%;
    margin: 12px 0;
    border: none;
    border-top: 1px solid #dee2e6;
  }

  &__description {
    color: #495057;
    font-size: 13px;
    font-weight: 400;
    line-height: 19px;
    margin-bottom: 12px;
  }

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: auto;
    color: #028ded;
    font-size: 13px;
    font-weight: 600;
    text-decoration: underline;
  }

  &__footer {
    background: #ecf0f3;
    border-top: 1px solid #dee2e6;
    padding: 16px;

    strong {
      color: #212529;
      font-size: 13px;
      font-weight: 700;
      line-height: 19px;
    }
  }
}

.buttons {
  margin-top: 24px;
}

.nav-btn {
  min-width: 140px;
  padding: 12px 20px;
}
</style>
